import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LanguageContext } from '../Components/LanguageContext';
import { fetchWarrantyBySerial } from '../api/publicApi';
import handleTransformLang from '../functions/handleTransformLang';
import Layout from '../Layout';
import supportWarrantyPage from '../lang/pages/supportWarranty.json';

const INPUT_CLASS =
  'block h-full w-full appearance-none border-0 bg-transparent px-6 text-[17px] text-text_secondary transition placeholder:text-[#95a0b1] focus:border-transparent focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0';

const SEARCH_WRAPPER_CLASS =
  'group/search flex h-[3.85rem] overflow-hidden border border-[#b7beca] bg-white transition focus-within:border-bg_secondary focus-within:ring-1 focus-within:ring-bg_secondary';

const normalizeWarrantyList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  const arrayKeys = ['data', 'items', 'results', 'result'];

  for (const key of arrayKeys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  for (const key of arrayKeys) {
    if (payload?.[key] && typeof payload[key] === 'object') {
      const nested = normalizeWarrantyList(payload[key]);
      if (nested.length > 0) return nested;
    }
  }

  if (payload?.serialNumber !== undefined || payload?.stockName !== undefined) {
    return [payload];
  }

  return [];
};

const mapWarrantyRow = (item, index) => ({
  id: String(item?.serialNumber ?? item?.stockId ?? index),
  serialNumber: String(item?.serialNumber ?? '-'),
  stockName: String(item?.stockName ?? '-'),
  merchantName: String(item?.merchantName ?? '-'),
  warrantyStartDate: String(item?.warrantyStartDate ?? '-'),
  warrantyEndDate: String(item?.warrantyEndDate ?? '-'),
  warrantyMonth: Number(item?.warrantyMonth ?? 0),
});

export default function SupportWarranty() {
  return (
    <Layout headData={supportWarrantyPage.head}>
      <InnerPage />
    </Layout>
  );
}

function InnerPage() {
  const { language } = useContext(LanguageContext);
  const [pageContent, setPageContent] = useState(
    handleTransformLang(supportWarrantyPage)
  );
  const [serialNumber, setSerialNumber] = useState('');
  const [rows, setRows] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setPageContent(handleTransformLang(supportWarrantyPage));
  }, [language]);

  const handleSearch = async () => {
    const trimmedSerial = serialNumber.trim();
    if (!trimmedSerial) return;

    setHasSearched(true);
    setIsLoading(true);
    setErrorMessage('');

    try {
      const payload = await fetchWarrantyBySerial(trimmedSerial);
      const nextRows = normalizeWarrantyList(payload).map(mapWarrantyRow);
      setRows(nextRows);
    } catch {
      setRows([]);
      setErrorMessage(pageContent.states.error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatMonthLabel = (count) => {
    if (!count) return '-';
    if (count % 12 === 0) {
      const years = count / 12;
      const yearSuffix = years === 1
        ? pageContent.yearSuffixSingular
        : pageContent.yearSuffixPlural;
      return `${years} ${yearSuffix}`;
    }
    return `${count} ${pageContent.monthSuffix}`;
  };

  return (
    <section className="bg-white pb-28 pt-10 lg:pb-32 lg:pt-12">
      <div className="container mx-auto max-w-8xl px-4">
        <div className="flex items-start justify-between gap-8">
          <Link to="/" className="inline-flex">
            <img
              src="/images/logo.svg"
              alt="Bulutmarket"
              className="h-24 w-auto lg:h-28"
            />
          </Link>
        </div>

        <div className="mx-auto mt-24 max-w-[74rem] text-center lg:mt-32">
          <h1 className="text-[32px] font-bold leading-tight text-text_primary">
            {pageContent.title}
          </h1>

          <p className="mx-auto mt-8 max-w-[66rem] whitespace-pre-line text-[17px] leading-[1.75] text-text_secondary">
            {pageContent.description}
          </p>

          <div className="mx-auto mt-24 max-w-[65.25rem]">
            <div className={SEARCH_WRAPPER_CLASS}>
              <input
                type="text"
                maxLength={100}
                value={serialNumber}
                placeholder={pageContent.placeholder}
                className={INPUT_CLASS}
                onChange={(event) => setSerialNumber(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleSearch();
                  }
                }}
              />

              <button
                type="button"
                className="group/button relative h-full w-[4.72rem] shrink-0 focus:outline-none focus-visible:outline-none"
                aria-label={pageContent.buttonLabel}
                onClick={handleSearch}
              >
                <img
                  src="/images/support/searchbox_zemin.svg"
                  alt=""
                  className="absolute inset-0 h-full w-full object-fill transition-opacity duration-200 group-hover/button:opacity-0 group-focus-within/search:opacity-0"
                />
                <img
                  src="/images/support/searchbox_zemin_active.svg"
                  alt=""
                  className="absolute inset-0 h-full w-full object-fill opacity-0 transition-opacity duration-200 group-hover/button:opacity-100 group-focus-within/search:opacity-100"
                />
                <img
                  src="/images/support/search_box_icon.svg"
                  alt=""
                  className="absolute left-1/2 top-1/2 z-10 h-7 w-7 -translate-x-1/2 -translate-y-1/2 transition-all duration-200 group-hover/button:opacity-0 group-focus-within/search:opacity-0"
                />
                <img
                  src="/images/support/search_box_icon_active.svg"
                  alt=""
                  className="absolute left-1/2 top-1/2 z-10 h-7 w-7 -translate-x-1/2 -translate-y-1/2 opacity-0 transition-all duration-200 group-hover/button:scale-[1.05] group-hover/button:opacity-100 group-focus-within/search:scale-[1.05] group-focus-within/search:opacity-100"
                />
              </button>
            </div>

            {hasSearched ? (
              <div className="mt-8 text-left">
                {isLoading ? (
                  <div className="border border-[#d7dce6] bg-white px-6 py-8 text-[17px] text-text_secondary">
                    {pageContent.states.loading}
                  </div>
                ) : errorMessage ? (
                  <div className="border border-[#d7dce6] bg-white px-6 py-8 text-[17px] text-[#d56152]">
                    {errorMessage}
                  </div>
                ) : rows.length === 0 ? (
                  <div className="border border-[#d7dce6] bg-white px-6 py-8 text-[17px] text-text_secondary">
                    {pageContent.states.empty}
                  </div>
                ) : (
                  <div className="mt-20 space-y-14 lg:mt-24">
                    {rows.map((row, index) => (
                      <div key={row.id}>
                        {rows.length > 1 ? (
                          <p className="mb-6 text-[15px] font-semibold uppercase tracking-[0.04em] text-text_secondary">
                            {pageContent.recordLabel} {index + 1} - {pageContent.recordSerialLabel}:{' '}
                            {row.serialNumber}
                          </p>
                        ) : null}

                        <div className="max-w-[76rem]">
                          <p className="text-[22px] font-bold text-text_primary">
                            {pageContent.resultTitle}
                          </p>

                          <div className="mt-12 grid gap-x-28 gap-y-14 lg:grid-cols-[1.55fr_0.95fr]">
                            <div className="space-y-4">
                              <WarrantyLineItem
                                label={pageContent.columns.productName}
                                value={row.stockName}
                                hasBorder
                              />
                              <WarrantyLineItem
                                label={pageContent.columns.companyName}
                                value={row.merchantName}
                                hasBorder
                              />
                              <WarrantyLineItem
                                label={pageContent.columns.warrantyDuration}
                                value={formatMonthLabel(row.warrantyMonth)}
                              />
                            </div>

                            <div className="space-y-4">
                              <WarrantyLineItem
                                label={pageContent.columns.warrantyStart}
                                value={row.warrantyStartDate}
                                hasBorder
                              />
                              <WarrantyLineItem
                                label={pageContent.columns.warrantyEnd}
                                value={row.warrantyEndDate}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function WarrantyLineItem({ label, value, hasBorder = false }) {
  return (
    <div className={hasBorder ? 'border-b border-[#b8bec9] pb-3' : ''}>
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2 text-[17px] leading-tight lg:flex-nowrap">
        <span className="shrink-0 font-bold text-bg_primary">{label}</span>
        <span className="text-[17px] text-text_secondary">{value || '-'}</span>
      </div>
    </div>
  );
}
